import { Injectable, inject } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import { ConfirmModal, ConfirmModalData } from './confirm-modal';

export interface ModalOptions<D = unknown> {
  /** Payload for the modal component, read there with `inject(MAT_DIALOG_DATA)`. */
  data?: D;

  /**
   * Blocks Escape and backdrop click. Off by default, because dismissing a
   * dialog that way is what users expect; turn it on for a form that can't
   * afford a stray dismissal, and pair it with `[closable]="false"` on the
   * `sci-modal` so the header doesn't offer a ✕ that contradicts it.
   */
  disableClose?: boolean;
}

/**
 * The way features open a modal:
 *
 * ```ts
 * private readonly modal = inject(ModalService);
 *
 * const ref = this.modal.open<CrewFormModal, CrewMember, CrewMember>(CrewFormModal, {
 *   data: member,
 *   disableClose: true,
 * });
 * const saved = await firstValueFrom(ref.afterClosed());
 * ```
 *
 * There is deliberately no `size` here: size belongs to the `sci-modal` element
 * inside the opened component, which is the one thing that knows how much room
 * its content needs. Putting it in the config too would be a second source of
 * truth for the same measurement.
 *
 * Transient notifications are not this service's job — `SnackbarService` owns
 * those, and that is why there is no `alert()`.
 */
@Injectable({ providedIn: 'root' })
export class ModalService {
  private readonly matDialog = inject(MatDialog);

  /**
   * Opens `component` as a modal, closing whatever modal is already open.
   *
   * Modals do not stack in this app: a second one opened over the first
   * replaces it. The displaced modal's `afterClosed()` therefore emits
   * `undefined` — the same value a dismissal produces — so a caller awaiting
   * its result must treat that as "cancelled".
   */
  open<C, D = unknown, R = unknown>(
    component: ComponentType<C>,
    options: ModalOptions<D> = {},
  ): MatDialogRef<C, R> {
    const { data, disableClose } = options;

    this.matDialog.closeAll();

    return this.matDialog.open<C, D, R>(component, {
      data,
      // Spread conditionally: Material merges this object over the app-wide
      // defaults, so an explicit `disableClose: undefined` would replace the
      // default rather than fall through to it.
      ...(disableClose === undefined ? {} : { disableClose }),
    });
  }

  /**
   * Asks a yes/no question and resolves to what the user chose:
   *
   * ```ts
   * const ok = await this.modal.confirm({
   *   title: 'views.crew.delete.title',
   *   message: 'views.crew.delete.message',
   *   confirmText: 'general.buttons.delete',
   *   tone: 'danger',
   * });
   * if (!ok) return;
   * ```
   *
   * The copy is translation keys, not final text — there is no `translate` pipe
   * in a TypeScript call site, so `ConfirmModal` resolves them.
   *
   * Every way out that isn't the confirm button resolves `false`: Cancel, the
   * ✕, Escape, a backdrop click, and being displaced by another modal.
   */
  async confirm(data: ConfirmModalData): Promise<boolean> {
    const ref = this.open<ConfirmModal, ConfirmModalData, boolean>(ConfirmModal, { data });

    return (await firstValueFrom(ref.afterClosed())) ?? false;
  }
}
